/**
 * @fileoverview API client wrapper for making requests to Salla API.
 */

/**
 * Base URL for Salla API.
 */
const API_BASE_URL = "https://api.salla.dev";

/**
 * Options for API requests.
 */
export interface ApiRequestOptions {
  /** Request method */
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  /** Request headers */
  headers?: Record<string, string>;
  /** Request body */
  body?: unknown;
  /** Request timeout in milliseconds */
  timeout?: number;
}

/**
 * API error class for handling API request failures.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Make a request to the Salla API.
 *
 * @param endpoint - API endpoint path (e.g., "/exchange-authority/v1/introspect")
 * @param options - Request options
 * @returns Promise that resolves with the response data
 * @throws {ApiError} If the request fails
 *
 * @example
 * ```typescript
 * const response = await apiRequest("/exchange-authority/v1/introspect", {
 *   method: "POST",
 *   headers: { "S-Source": "app-id", "Content-Type": "application/json" },
 *   body: { token: "xxx", env: "prod" }
 * });
 * ```
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { method = "GET", headers = {}, body, timeout = 30000 } = options;

  const url = `${API_BASE_URL}${endpoint}`;

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  try {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Parse response
    let data: unknown;
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Check if response is successful
    if (!response.ok) {
      throw new ApiError(
        `API request failed: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.name === "AbortError") {
        throw new ApiError(`Request timeout after ${timeout}ms`);
      }
      throw new ApiError(`Request failed: ${error.message}`);
    }

    throw new ApiError("Unknown error occurred");
  }
}
